import { useEffect, useLayoutEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { useStoreActions } from '@/stores/store';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';
import { DashboardHeader } from '@/screens/HomeTherapist/DashboardHeader';
import { DashboardSets } from '@/screens/HomeTherapist/DashboardSets';
import { DashboardSettings } from '@/screens/HomeTherapist/DashboardSettings';
import { Icon } from '@/components/Icon';
import { NoPatient } from '@/screens/HomeTherapist/NoPatient';
import Card from '@/components/ui/therapist/Card';
import {
  dismissLicenseWarning,
  snoozeLicenseWarning,
  fetchPatient,
  getPatientLicenseStatus,
} from '@/api/services/patient';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { Patient } from '@/stores/store.types';
import LicenseBanner from './LicenseBanner';
import { LicenseStatus } from '@/api/services/user';
import {
  showDigaPatientWarning,
  showSelfPayerWarning,
} from '@/utils/licenseCheck';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

export function PatientProfile(
  props: StackScreenProps<TherapistStackParamList, 'PatientProfile'>,
) {
  const { id, patients } = props.route.params;
  const { changeID, changeSelectedPatient, fetchPatients } =
    useDashboardContext();
  const { triggerRefresh } = useStoreActions((actions) => ({
    triggerRefresh: actions.triggerRefresh,
  }));
  const { showNotification } = useNotification();
  const { navigation } = props;
  const isFocused = useIsFocused();
  const selectedPatient = patients?.find((patient) => patient._id === id);
  const [localSelectedPatient, setLocalSelectedPatient] = useState<
    Patient | undefined
  >(patients?.find((patient) => patient._id === id));

  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(
    null,
  );
  const [showLicenseWarning, setShowLicenseWarning] = useState<boolean | null>(
    null,
  );

  const {
    textsets,
    sentencesets,
    wordsets,
    _id,
    firstName,
    lastName,
    avatar,
    diagnosis,
    code,
    activeTextsets,
    activeSentencesets,
    activeWordsets,
    biography,
  } = localSelectedPatient ?? {};

  const setPatient = async () => {
    if (localSelectedPatient) {
      await changeSelectedPatient(localSelectedPatient);
      await changeID(localSelectedPatient._id, true, patients);

      const license = await getPatientLicenseStatus(localSelectedPatient._id);

      setLicenseStatus(license);

      if (!license || license.daysLeft === undefined) {
        return;
      }

      if (license?.isDismissed) {
        setShowLicenseWarning(false);

        return;
      }

      if (
        !license.snoozedAt &&
        (showDigaPatientWarning(license) || showSelfPayerWarning(license))
      ) {
        setShowLicenseWarning(true);
      } else if (
        license.snoozedAt &&
        new Date().getTime() - new Date(license.snoozedAt).getTime() >
          24 * 60 * 60 * 1000
      ) {
        setShowLicenseWarning(true);

        return;
      }
    }
  };

  const refreshPatient = async () => {
    const hasInternet = await hasInternetAsync();
    if (!hasInternet) {
      return;
    }

    const refreshedPatient = await fetchPatient(id);
    setLocalSelectedPatient(refreshedPatient);
  };

  useEffect(() => {
    setPatient();
  }, [id, patients, localSelectedPatient]);

  useEffect(() => {
    if (isFocused) {
      refreshPatient();
      triggerRefresh();
    }
  }, [isFocused]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <AppHeaderAction
          primary
          onPress={() => navigation.navigate('Home')}
          icon={<Icon name="chevronLeft" />}
          text="Zurück"
        />
      ),
    });
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: `Profil: ${firstName} ${lastName}`,
    });
  }, [firstName, lastName]);

  const onRemindTomorrow = async () => {
    try {
      await snoozeLicenseWarning(localSelectedPatient?._id!);
      setShowLicenseWarning(false);
    } catch (error) {
      showNotification({
        title: 'Fehler beim Pausieren',
        message:
          'Die Erinnerung konnte nicht pausiert werden. Bitte versuchen Sie es erneut.',
        type: 'error',
      });
    }
  };

  const onHideForever = async () => {
    try {
      await dismissLicenseWarning(localSelectedPatient?._id!);
      setShowLicenseWarning(false);
    } catch (error) {
      showNotification({
        title: 'Fehler beim Deaktivieren',
        message:
          'Die Erinnerung konnte nicht deaktiviert werden. Bitte versuchen Sie es erneut.',
        type: 'error',
      });
    }
  };

  return (
    <BodyWrapper bodyStyle={{ width: '90%' }}>
      {!id && patients?.length === 0 ? (
        <NoPatient />
      ) : (
        <ScrollView style={{ width: '100%' }}>
          <View style={{ gap: 32 }}>
            {!!licenseStatus && showLicenseWarning !== null ? (
              <LicenseBanner
                showLicenseWarning={
                  !!showLicenseWarning &&
                  (showDigaPatientWarning(licenseStatus) ||
                    showSelfPayerWarning(licenseStatus))
                }
                license={{ daysLeft: licenseStatus.daysLeft }}
                onRemindTomorrow={onRemindTomorrow}
                onHideForever={onHideForever}
              />
            ) : null}

            <Card>
              <DashboardHeader
                avatar={avatar}
                _id={_id}
                fullName={(firstName ?? '') + ' ' + (lastName ?? '')}
                diagnosis={diagnosis}
                biography={biography}
                fetchPatients={fetchPatients}
                navigation={navigation}
              />
            </Card>
            <View>
              <Card title="Eigentraining">
                <DashboardSettings
                  selectedPatient={selectedPatient}
                  code={code}
                  selectedID={_id}
                  activeTextsets={activeTextsets ?? []}
                  activeSentencesets={activeSentencesets ?? []}
                  activeWordsets={activeWordsets ?? []}
                />
              </Card>
            </View>
            <View>
              <DashboardSets
                textsets={textsets ?? []}
                wordsets={wordsets ?? []}
                sentencesets={sentencesets ?? []}
                refreshPatient={refreshPatient}
              />
            </View>
          </View>
        </ScrollView>
      )}
    </BodyWrapper>
  );
}
